import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, AlertTriangle, Shield, CheckCircle, XCircle, 
  Loader, X, RefreshCw, Eye, Ban, TrendingUp, Clock, DollarSign,
  User, Store, Flag, Play, Save, AlertCircle
} from 'lucide-react';
import adminService, { 
  FlaggedTransaction, FlaggedTransactionsResponse, FraudStatistics,
  GetFlaggedTransactionsParams, FlagTransactionRequest, ResolveFraudCaseRequest,
  FreezeForFraudRequest, AutoDetectFraudResponse, FraudType, FraudStatus,
  FraudSeverity, FraudResolution, Transaction
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'overview' | 'flagged-transactions' | 'auto-detect';

const FraudDetectionManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Statistics
  const [fraudStats, setFraudStats] = useState<FraudStatistics | null>(null);

  // Flagged Transactions
  const [flaggedTransactions, setFlaggedTransactions] = useState<FlaggedTransaction[]>([]);
  const [flaggedPage, setFlaggedPage] = useState(1);
  const [flaggedTotalPages, setFlaggedTotalPages] = useState(1);
  const [flaggedTotal, setFlaggedTotal] = useState(0);
  const [flaggedFilters, setFlaggedFilters] = useState<GetFlaggedTransactionsParams>({
    page: 1,
    limit: 20,
  });

  // Auto Detect
  const [autoDetectResult, setAutoDetectResult] = useState<AutoDetectFraudResponse | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [autoFlag, setAutoFlag] = useState(false);

  // Modals
  const [flagModal, setFlagModal] = useState(false);
  const [resolveModal, setResolveModal] = useState(false);
  const [freezeModal, setFreezeModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<FlaggedTransaction | null>(null);
  const [processing, setProcessing] = useState(false);

  // Forms
  const [flagForm, setFlagForm] = useState<FlagTransactionRequest>({
    transaction_id: '',
    fraud_type: 'abnormal_pattern',
    reason: '',
    severity: 'medium',
  });

  const [resolveForm, setResolveForm] = useState<ResolveFraudCaseRequest>({
    transaction_id: '',
    resolution: 'false_positive',
    resolution_notes: '',
    action_taken: '',
  });

  const [freezeForm, setFreezeForm] = useState<FreezeForFraudRequest>({
    entity_type: 'business',
    entity_id: 0,
    reason: '',
    duration_days: undefined,
  });

  useEffect(() => {
    fetchFraudStats();
    if (activeTab === 'flagged-transactions') {
      fetchFlaggedTransactions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'flagged-transactions') {
      fetchFlaggedTransactions();
    }
  }, [flaggedPage, flaggedFilters]);

  const fetchFraudStats = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      const stats = await adminService.getFraudStatistics();
      setFraudStats(stats);
      setError(null);
    } catch (error: any) {
      let errorMessage = 'Failed to fetch fraud statistics';
      
      // Handle network errors specifically
      if (error.isNetworkError || error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') {
        if (error.isConnectionRefused || error.code === 'ERR_CONNECTION_REFUSED') {
          errorMessage = error.userMessage || 'Cannot connect to server. Please ensure the backend server is running.';
        } else {
          errorMessage = error.userMessage || 'Network error - please check your connection and server status.';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching fraud statistics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchFlaggedTransactions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getFraudFlaggedTransactions({
        ...flaggedFilters,
        page: flaggedPage,
      });
      setFlaggedTransactions(data.transactions);
      setFlaggedPage(data.pagination.current_page);
      setFlaggedTotalPages(data.pagination.total_pages);
      setFlaggedTotal(data.pagination.total_transactions);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch flagged transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDetect = async () => {
    try {
      setDetecting(true);
      const result = await adminService.autoDetectFraud({
        time_range: timeRange,
        auto_flag: autoFlag,
      });
      setAutoDetectResult(result);
      if (result.auto_flagged) {
        toast.success(`Auto-detected and flagged ${result.detected_frauds.total_detected} fraud cases!`);
        fetchFlaggedTransactions();
      } else {
        toast.success(`Detected ${result.detected_frauds.total_detected} potential fraud cases`);
      }
      fetchFraudStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to detect fraud');
    } finally {
      setDetecting(false);
    }
  };

  const handleFlagTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.flagTransaction(flagForm);
      toast.success('Transaction flagged successfully!');
      setFlagModal(false);
      resetFlagForm();
      fetchFlaggedTransactions();
      fetchFraudStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to flag transaction');
    } finally {
      setProcessing(false);
    }
  };

  const handleResolveFraud = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTransaction) return;
    
    try {
      setProcessing(true);
      await adminService.resolveFraudCase({
        ...resolveForm,
        transaction_id: selectedTransaction.transaction_id,
      });
      toast.success('Fraud case resolved successfully!');
      setResolveModal(false);
      resetResolveForm();
      fetchFlaggedTransactions();
      fetchFraudStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resolve fraud case');
    } finally {
      setProcessing(false);
    }
  };

  const handleFreezeEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.freezeForFraud(freezeForm);
      toast.success(`${freezeForm.entity_type} frozen successfully!`);
      setFreezeModal(false);
      resetFreezeForm();
      fetchFraudStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to freeze entity');
    } finally {
      setProcessing(false);
    }
  };

  const openResolveModal = (transaction: FlaggedTransaction) => {
    setSelectedTransaction(transaction);
    setResolveForm({
      transaction_id: transaction.transaction_id,
      resolution: 'false_positive',
      resolution_notes: '',
      action_taken: '',
    });
    setResolveModal(true);
  };

  const openFreezeModal = (transaction: FlaggedTransaction) => {
    setSelectedTransaction(transaction);
    setFreezeForm({
      entity_type: 'business',
      entity_id: transaction.business_id || 0,
      reason: `Fraud confirmed in transaction: ${transaction.transaction_id}`,
      duration_days: 30,
    });
    setFreezeModal(true);
  };

  const resetFlagForm = () => {
    setFlagForm({
      transaction_id: '',
      fraud_type: 'abnormal_pattern',
      reason: '',
      severity: 'medium',
    });
  };

  const resetResolveForm = () => {
    setResolveForm({
      transaction_id: '',
      resolution: 'false_positive',
      resolution_notes: '',
      action_taken: '',
    });
    setSelectedTransaction(null);
  };

  const resetFreezeForm = () => {
    setFreezeForm({
      entity_type: 'business',
      entity_id: 0,
      reason: '',
      duration_days: undefined,
    });
    setSelectedTransaction(null);
  };

  const getSeverityBadge = (severity: FraudSeverity) => {
    const colors = {
      low: 'bg-blue-100 text-blue-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[severity]}`}>
        {severity.toUpperCase()}
      </span>
    );
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Pending</span>;
    }
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      under_review: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700',
      false_positive: 'bg-gray-100 text-gray-700',
      confirmed_fraud: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || 'bg-secondary-200 text-secondary-700'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const fraudTypeOptions: FraudType[] = [
    'abnormal_pattern', 'repeated_failures', 'rapid_payments', 
    'amount_mismatch', 'location_mismatch', 'other'
  ];

  const severityOptions: FraudSeverity[] = ['low', 'medium', 'high', 'critical'];
  const resolutionOptions: FraudResolution[] = ['resolved', 'false_positive', 'confirmed_fraud'];

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Fraud Detection Management</h1>
          <p className="text-secondary-600">Monitor and manage fraud detection and prevention</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              fetchFraudStats(false);
              if (activeTab === 'flagged-transactions') fetchFlaggedTransactions();
            }}
            disabled={refreshing || loading}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              resetFlagForm();
              setFlagModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all"
          >
            <Flag size={20} />
            <span>Flag Transaction</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="border-b border-secondary-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <TrendingUp size={20} />
              <span>Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('flagged-transactions')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'flagged-transactions'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <AlertTriangle size={20} />
              <span>Flagged Transactions</span>
            </button>
            <button
              onClick={() => setActiveTab('auto-detect')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'auto-detect'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Shield size={20} />
              <span>Auto Detect</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {fraudStats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-red-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Total Flagged</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">
                            {fraudStats.overview.total_flagged || 0}
                          </p>
                        </div>
                        <Flag className="text-red-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-yellow-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Pending Review</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">
                            {fraudStats.overview.pending_review || 0}
                          </p>
                        </div>
                        <Clock className="text-yellow-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-green-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Confirmed Fraud</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">
                            {fraudStats.overview.confirmed_fraud || 0}
                          </p>
                        </div>
                        <AlertTriangle className="text-green-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-blue-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">False Positives</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">
                            {fraudStats.overview.false_positives || 0}
                          </p>
                        </div>
                        <CheckCircle className="text-blue-500" size={32} />
                      </div>
                    </div>
                  </div>

                  {fraudStats.by_type && fraudStats.by_type.length > 0 && (
                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <h3 className="text-lg font-bold text-secondary-900 mb-4">Fraud by Type</h3>
                      <div className="space-y-3">
                        {fraudStats.by_type.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                            <span className="font-semibold text-secondary-900 capitalize">
                              {item.fraud_type.replace('_', ' ')}
                            </span>
                            <span className="text-lg font-bold text-primary-600">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="text-yellow-500 mb-4" size={48} />
                  <p className="text-secondary-600 text-lg font-semibold mb-2">Unable to load fraud statistics</p>
                  {error && (
                    <div className="mb-4 max-w-md text-center">
                      <p className="text-red-500 text-sm mb-2">{error}</p>
                      {error.includes('Cannot connect to server') && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                          <p className="text-xs text-yellow-800 font-semibold mb-1">Troubleshooting:</p>
                          <ul className="text-xs text-yellow-700 space-y-1 list-disc list-inside">
                            <li>Ensure the backend server is running</li>
                            <li>Check if the server is accessible at the configured URL</li>
                            <li>Verify your network connection</li>
                            <li>Check browser console for detailed error information</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => fetchFraudStats()}
                    disabled={loading || refreshing}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={18} className={loading || refreshing ? 'animate-spin' : ''} />
                    <span>Retry</span>
                  </button>
                </div>
              )}
            </>
          )}

          {/* Flagged Transactions Tab */}
          {activeTab === 'flagged-transactions' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <select
                  value={flaggedFilters.fraud_type || ''}
                  onChange={(e) => setFlaggedFilters({...flaggedFilters, fraud_type: e.target.value as FraudType || undefined, page: 1})}
                  className="px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                >
                  <option value="">All Types</option>
                  {fraudTypeOptions.map(type => (
                    <option key={type} value={type}>{type.replace('_', ' ')}</option>
                  ))}
                </select>
                <select
                  value={flaggedFilters.status || ''}
                  onChange={(e) => setFlaggedFilters({...flaggedFilters, status: e.target.value as FraudStatus || undefined, page: 1})}
                  className="px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="false_positive">False Positive</option>
                  <option value="confirmed_fraud">Confirmed Fraud</option>
                </select>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Transaction ID</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">User/Business</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Amount</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Fraud Type</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Severity</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Flagged At</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {flaggedTransactions.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-6 py-12 text-center text-secondary-600">
                              No flagged transactions found
                            </td>
                          </tr>
                        ) : (
                          flaggedTransactions.map((transaction) => (
                            <tr key={transaction.transaction_id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <code className="text-sm font-mono text-secondary-900">{transaction.transaction_id}</code>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  {transaction.user_name && (
                                    <p className="text-sm text-secondary-900 flex items-center space-x-1">
                                      <User size={14} />
                                      <span>{transaction.user_name}</span>
                                    </p>
                                  )}
                                  {transaction.business_name && (
                                    <p className="text-sm text-secondary-600 flex items-center space-x-1">
                                      <Store size={14} />
                                      <span>{transaction.business_name}</span>
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="font-semibold text-secondary-900">${transaction.amount}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold capitalize">
                                  {transaction.fraud_type.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {getSeverityBadge(transaction.severity)}
                              </td>
                              <td className="px-6 py-4">
                                {getStatusBadge(transaction.resolution)}
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(transaction.flagged_at).toLocaleDateString()}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center justify-end space-x-2">
                                  {!transaction.resolution && (
                                    <>
                                      <button
                                        onClick={() => openResolveModal(transaction)}
                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                        title="Resolve"
                                      >
                                        <CheckCircle size={18} />
                                      </button>
                                      <button
                                        onClick={() => openFreezeModal(transaction)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                        title="Freeze Entity"
                                      >
                                        <Ban size={18} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {flaggedTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {flaggedPage} of {flaggedTotalPages} ({flaggedTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setFlaggedPage(p => Math.max(1, p - 1))}
                          disabled={flaggedPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setFlaggedPage(p => Math.min(flaggedTotalPages, p + 1))}
                          disabled={flaggedPage === flaggedTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Auto Detect Tab */}
          {activeTab === 'auto-detect' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-lg font-bold text-secondary-900 mb-4">Auto Fraud Detection</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">Time Range</label>
                    <select
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
                      className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    >
                      <option value="1h">Last 1 Hour</option>
                      <option value="24h">Last 24 Hours</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 mt-8">
                      <input
                        type="checkbox"
                        checked={autoFlag}
                        onChange={(e) => setAutoFlag(e.target.checked)}
                        className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-semibold text-secondary-700">Auto-flag detected fraud</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={handleAutoDetect}
                  disabled={detecting}
                  className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                >
                  <Play size={20} />
                  <span>{detecting ? 'Detecting...' : 'Run Fraud Detection'}</span>
                </button>
              </div>

              {autoDetectResult && (
                <div className="bg-white rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-4">Detection Results</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-secondary-600 mb-1">Abnormal Patterns</p>
                      <p className="text-2xl font-bold text-primary-600">
                        {autoDetectResult.detected_frauds.abnormal_patterns.length}
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-sm text-secondary-600 mb-1">Repeated Failures</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {autoDetectResult.detected_frauds.repeated_failures.length}
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-sm text-secondary-600 mb-1">Rapid Payments</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {autoDetectResult.detected_frauds.rapid_payments.length}
                      </p>
                    </div>
                  </div>
                  <div className="bg-primary-50 rounded-lg p-4">
                    <p className="text-sm text-secondary-600 mb-1">Total Detected</p>
                    <p className="text-3xl font-bold text-primary-600">
                      {autoDetectResult.detected_frauds.total_detected}
                    </p>
                    {autoDetectResult.auto_flagged && (
                      <p className="text-sm text-green-600 mt-2">✓ Auto-flagged successfully</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Flag Transaction Modal */}
      {flagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">Flag Transaction</h2>
                <button
                  onClick={() => {
                    setFlagModal(false);
                    resetFlagForm();
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleFlagTransaction} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Transaction ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={flagForm.transaction_id}
                    onChange={(e) => setFlagForm({...flagForm, transaction_id: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none font-mono"
                    placeholder="TXN123456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Fraud Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={flagForm.fraud_type}
                    onChange={(e) => setFlagForm({...flagForm, fraud_type: e.target.value as FraudType})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {fraudTypeOptions.map(type => (
                      <option key={type} value={type}>{type.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={flagForm.severity}
                    onChange={(e) => setFlagForm({...flagForm, severity: e.target.value as FraudSeverity})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {severityOptions.map(severity => (
                      <option key={severity} value={severity}>{severity}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    value={flagForm.reason}
                    onChange={(e) => setFlagForm({...flagForm, reason: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={4}
                    placeholder="Enter detailed reason for flagging this transaction (minimum 10 characters)"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFlagModal(false);
                      resetFlagForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Flagging...' : 'Flag Transaction'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Fraud Modal */}
      {resolveModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">Resolve Fraud Case</h2>
                <button
                  onClick={() => {
                    setResolveModal(false);
                    resetResolveForm();
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="mb-4 p-4 bg-secondary-50 rounded-lg">
                <p className="text-sm text-secondary-600 mb-1">Transaction ID</p>
                <p className="font-mono font-semibold text-secondary-900">{selectedTransaction.transaction_id}</p>
                <p className="text-sm text-secondary-600 mt-2 mb-1">Fraud Type</p>
                <p className="font-semibold text-secondary-900 capitalize">{selectedTransaction.fraud_type.replace('_', ' ')}</p>
                <p className="text-sm text-secondary-600 mt-2 mb-1">Reason</p>
                <p className="text-secondary-900">{selectedTransaction.fraud_reason}</p>
              </div>
              <form onSubmit={handleResolveFraud} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Resolution <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={resolveForm.resolution}
                    onChange={(e) => setResolveForm({...resolveForm, resolution: e.target.value as FraudResolution})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {resolutionOptions.map(resolution => (
                      <option key={resolution} value={resolution}>{resolution.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Resolution Notes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    value={resolveForm.resolution_notes}
                    onChange={(e) => setResolveForm({...resolveForm, resolution_notes: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={4}
                    placeholder="Enter detailed resolution notes (minimum 10 characters)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Action Taken</label>
                  <textarea
                    value={resolveForm.action_taken || ''}
                    onChange={(e) => setResolveForm({...resolveForm, action_taken: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter any actions taken (optional)"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setResolveModal(false);
                      resetResolveForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Resolving...' : 'Resolve Case'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Freeze Entity Modal */}
      {freezeModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Ban className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Freeze Entity</h2>
                  <p className="text-sm text-secondary-600">Freeze business or user due to fraud</p>
                </div>
              </div>
              <form onSubmit={handleFreezeEntity} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Entity Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={freezeForm.entity_type}
                    onChange={(e) => setFreezeForm({
                      ...freezeForm, 
                      entity_type: e.target.value as 'business' | 'user',
                      entity_id: e.target.value === 'business' ? (selectedTransaction.business_id || 0) : (selectedTransaction.user_id || 0)
                    })}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="business">Business</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Entity ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    value={freezeForm.entity_id}
                    onChange={(e) => setFreezeForm({...freezeForm, entity_id: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    value={freezeForm.reason}
                    onChange={(e) => setFreezeForm({...freezeForm, reason: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={4}
                    placeholder="Enter reason for freezing (minimum 10 characters)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Duration (Days) - Optional</label>
                  <input
                    type="number"
                    min="1"
                    value={freezeForm.duration_days || ''}
                    onChange={(e) => setFreezeForm({...freezeForm, duration_days: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Leave empty for permanent freeze"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFreezeModal(false);
                      resetFreezeForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Freezing...' : 'Freeze Entity'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudDetectionManagement;

